<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import * as getters from '@/js/store/modules/getters'
import * as _ from 'lodash'
import * as actions from '@/js/store/modules/actions'
import {
  DynamicPathBuilder,
  DynamicTaggingForm,
} from '../../lib/dynamic-tagging'
// import QuillMentionInput from '@/js/components/common/QuillMentionInput.vue'
import { DATA_SCHEMA, EXAMPLE_DATA } from './samples'

const COMPONENT_VALUE_PATH = {
  draft: 'contentsHTML',
  'c-button': 'cta',
  'c-image': 'src',
  'card-item': '',
}

const predefinedTags = [
  'heading',
  'title',
  'summary',
  'cta',
  'description',
  'benefits',
  'features',
  'testimonial',
  'pricing',
  'contact',
  'about',
  'hero',
  'footer',
  'navigation',
]

export default {
  name: 'GenAIOptions',
  components: {
    // QuillMentionInput,
  },
  props: {
    mode: {
      type: String,
      default: 'LIGHT',
    },
    pathPrefix: {
      type: String,
      default: null,
    },
    selectorValueKey: {
      type: String,
      default: null,
    },
  },
  data() {
    const pathBuilder = new DynamicPathBuilder(DATA_SCHEMA, EXAMPLE_DATA)
    const formTagger = new DynamicTaggingForm(pathBuilder)
    return {
      predefinedTags,
      pathBuilder,
      formTagger,
    }
  },
  computed: {
    ...mapGetters({
      selected: getters.SELECTOR_SELECTED,
      user: getters.AUTH_GET_USER,
      isAdmin: getters.AUTH_GET_USER_IS_ADMIN,
    }),
    genAI() {
      if (this.pathPrefix) {
        return _.get(
          this.selected,
          `${this.pathPrefix}/dc`.replaceAll('/', '.'),
          {},
        )
      }
      return this.selected.dc || {}
    },
    isSectionSelected() {
      return this.selected.name === 'Block'
    },
    isBlockSelected() {
      return this.selected.name === 'Block'
    },
    promptJSON: {
      get() {
        return this.genAI.promptJSON || ''
      },
    },
    label: {
      get() {
        return this.genAI.label || ''
      },
    },
    // Dynamic tagging computed properties
    formLevels() {
      return this.formTagger?.getFormLevels() || []
    },
    currentPath() {
      return this.formTagger?.getCurrentPath() || ''
    },
    currentValue() {
      return this.formTagger?.getCurrentValue()
    },
    canSave() {
      return this.formTagger?.canCreateTag() || false
    },
    currentTag() {
      return this.formTagger?.getCurrentTag()
    },
  },
  mounted() {
    this.clearForm()
    this.genAI?.itemAttribute && this.loadTag(this.genAI)
  },
  methods: {
    ...mapMutations('genAI', {
      openGenAIDialog: 'openGenAIDialog',
    }),
    ...mapActions({
      updateProp: actions.SELECTOR_UPDATE_PROP,
    }),
    saveText(formattedDelta, formattedText) {
      return this.updateProp({
        key: this.pathPrefix ? `${this.pathPrefix}/dc` : 'dc',
        value: {
          ...this.genAI,
          promptJSON: formattedDelta,
          prompt: formattedText,
        },
      })
    },
    addGenAIField() {
      if (this.genAI.itemAttribute || this.genAI.description) return

      let data = {
        key: this.pathPrefix ? `${this.pathPrefix}/dc` : 'dc',
        value: {
          itemAttribute: '',
          id: this.selected.vnodeId,
        },
      }
      if (this.isBlockSelected) {
        data = {
          key: data.key,
          value: {
            ...data.value,
            label: '',
          },
        }
      } else {
        data = {
          key: data.key,
          value: {
            ...data.value,
            tags: [],
            maxLength: 0,
          },
        }
      }

      if (COMPONENT_VALUE_PATH[this.selected.nodeTag] !== undefined) {
        data.value.itemAttribute = `${
          COMPONENT_VALUE_PATH[this.selected.nodeTag]
        }`
      }
      if (this.selectorValueKey) {
        data.value.itemAttribute += `/${this.selectorValueKey}`
        data.value.itemAttribute = data.value.itemAttribute.replaceAll('//', '/')
      }
      this.updateProp(data)
    },
    removeGenAIField() {
      this.updateProp({
        key: this.pathPrefix ? `${this.pathPrefix}/dc` : 'dc',
        value: {},
      })
      this.clearForm()
    },
    onSelectionChange(level, value) {
      this.formTagger.updateSelection(level, value)
      this.$forceUpdate() // Force reactivity update
      this.saveTag()
    },
    getSelectedOptionPreview(level) {
      const selectedOption = level.options.find(
        (opt) => opt.value === level.selectedValue,
      )
      return selectedOption?.preview || ''
    },
    saveTag() {
      if (!this.canSave) return

      const tag = this.currentTag
      if (tag) {
        this.updateProp({
          key: this.pathPrefix ? `${this.pathPrefix}/dc` : 'dc',
          value: { ...this.genAI, ...tag },
        })
        this.$message.success(`Tag saved: ${tag.path}`)
      }
    },
    clearForm() {
      this.formTagger.clearForm()
      this.$forceUpdate()
    },
    loadTag(tag) {
      this.formTagger.loadExistingTag(tag)
      this.$forceUpdate()
    },
    toString(level) {
      return `${level.level}-${level.label}-${level.options.length}`
    },
  },
}
</script>

<template>
  <div>
    <div v-if="genAI.itemAttribute === undefined" class="mb-3">
      <cvt-button
        :outlined="true"
        :full-width="true"
        shape="rounded"
        color="light"
        :mode="mode"
        text="Assign A Variable"
        @click="addGenAIField"
      />
    </div>

    <div v-if="genAI.itemAttribute !== undefined" class="mb-3">
      <cvt-button
        :outlined="true"
        :full-width="true"
        shape="rounded"
        color="danger"
        :mode="mode"
        text="Remove Assigned Variable"
        @click="removeGenAIField"
      />
    </div>

    <div
      v-if="genAI.itemAttribute !== undefined && !isBlockSelected"
      class="row no-gutters mt-3"
    >
      <!-- Dynamic Tagging Section -->
      <div
        v-for="level in formLevels"
        :key="toString(level)"
        class="form-level mb-3"
      >
        <div class="form-group">
          <label>{{level.label}}</label>
          <select
            v-model="level.selectedValue"
            :class="{'form-control': true, 'is-invalid': level.selectedValue === null}"
            @change="onSelectionChange(level.level, level.selectedValue)"
          >
            <option value="" disabled>Choose {{ level.label.toLowerCase() }}...</option>
            <option
              v-for="option in level.options"
              :key="option.value"
              :value="option.value === 0? '0' : option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <!-- Show preview for selected option -->
          <div v-if="level.selectedValue !== null" class="option-preview mt-2">
            <small class="text-muted">
              <i class="fa fa-eye mr-1"></i>
              {{ getSelectedOptionPreview(level) }}
            </small>
          </div>
        </div>
      </div>

      <!-- Path preview -->
      <div
        v-if="currentPath"
        class="path-preview alert alert-info d-flex p-1 flex-column"
      >
        <strong>Path:</strong>
        <code class="bg-light p-1 rounded text-wrap">{{ currentPath }}</code>
      </div>
    </div>
    <div
      v-if="genAI.itemAttribute !== undefined && isBlockSelected"
      class="row no-gutters mt-3"
    >
      <div class="col-md-12">
        <cvt-input
          v-model="label"
          type="text"
          label="label"
          :clearable="false"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.prompt-label {
  color: #8f9bb3;
}

.text-wrap {
  word-break: break-all;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}
</style>
